<script lang="ts">
	import { onMount } from 'svelte'
	import { t } from 'svelte-i18n'
	import Modal from '../Modal.svelte'
	import {
		Button,
		ButtonType,
		ButtonSize,
		BaseInput,
		MarkdownInput,
		Checkbox,
		LoadingSpinner
	} from '@repo/commons/components'
	import {
		RELEASE_VERSION_PATTERN,
		toReleaseVersion,
		toDevelopmentVersion
	} from '@repo/commons/utils'

	export interface CreateVersionFormData {
		releaseVersion: string
		developmentVersion?: string
		changelog: string
		allowReleaseWithErrors: boolean
	}

	interface Props {
		visible?: boolean
		currentVersion?: string | undefined
		errorCount?: number
		loading?: boolean
		prefillLoading?: boolean
		showDevelopmentVersion?: boolean
		onConfirm: (data: CreateVersionFormData) => void
		onClose: () => void
	}

	let {
		visible = false,
		currentVersion = undefined,
		errorCount = 0,
		loading = false,
		prefillLoading = false,
		showDevelopmentVersion = false,
		onConfirm,
		onClose
	}: Props = $props()

	let form = $state({
		releaseVersion: '',
		developmentVersion: '',
		changelog: ''
	})

	let allowReleaseWithErrors = $state(false)
	let touchedFields = $state({ releaseVersion: false, developmentVersion: false })
	let isSubmitting = $state(false)

	const hasProjectErrors = $derived(errorCount > 0)

	const releaseErrorText = $derived.by(() => {
		if (!hasProjectErrors) return ''
		return $t(
			allowReleaseWithErrors
				? 'CreateVersionDialog.ReleaseAllowedWithErrors'
				: 'CreateVersionDialog.ReleaseBlockedWithErrors'
		).replace('{{ errorCount }}', errorCount.toString())
	})

	const releaseVersionError = $derived.by(() => {
		if (!touchedFields.releaseVersion && !isSubmitting) return ''
		if (!form.releaseVersion?.trim()) {
			return $t('CreateVersionDialog.Validation.VersionRequired')
		}
		if (!RELEASE_VERSION_PATTERN.test(form.releaseVersion)) {
			return $t('CreateVersionDialog.Validation.VersionFormat')
		}
		return ''
	})

	const devVersionError = $derived.by(() => {
		if (!showDevelopmentVersion) return ''
		if (!touchedFields.developmentVersion && !isSubmitting) return ''
		if (!form.developmentVersion?.trim()) {
			return $t('CreateVersionDialog.Validation.DevVersionRequired')
		}
		return ''
	})

	const isReleaseVersionValid = $derived(
		!!form.releaseVersion?.trim() && RELEASE_VERSION_PATTERN.test(form.releaseVersion)
	)

	const isSubmitDisabled = $derived(
		loading ||
			!isReleaseVersionValid ||
			(showDevelopmentVersion && !form.developmentVersion?.trim()) ||
			(hasProjectErrors && !allowReleaseWithErrors)
	)

	const handleReleaseVersionBlur = () => {
		touchedFields.releaseVersion = true
		if (!form.releaseVersion || !showDevelopmentVersion) return
		form.developmentVersion = toDevelopmentVersion(form.releaseVersion)
		touchedFields.developmentVersion = true
	}

	const handleCreate = () => {
		isSubmitting = true
		if (isSubmitDisabled) return
		onConfirm({
			releaseVersion: form.releaseVersion.trim(),
			...(showDevelopmentVersion ? { developmentVersion: form.developmentVersion.trim() } : {}),
			changelog: form.changelog.trim(),
			allowReleaseWithErrors
		})
	}

	const resetForm = () => {
		const releaseVersion = toReleaseVersion(currentVersion)
		form.releaseVersion = releaseVersion
		form.developmentVersion =
			showDevelopmentVersion && releaseVersion ? toDevelopmentVersion(releaseVersion) : ''
		form.changelog = ''
		allowReleaseWithErrors = false
		touchedFields = { releaseVersion: false, developmentVersion: false }
		isSubmitting = false
	}

	/** Reset when dialog opens or when `currentVersion` updates while open (e.g. async refetch from parent). */
	$effect(() => {
		if (!visible) return
		void currentVersion
		resetForm()
	})

	onMount(() => {
		resetForm()
	})
</script>

<Modal
	{visible}
	title={$t('CreateVersionDialog.Title')}
	showCloseButton={false}
	closeOnBackdropClick={false}
	onclose={onClose}
	styleProps="width: min(520px, calc(100vw - 2rem));"
>
	{#snippet children()}
		<div class="dialog-body">
			{#if hasProjectErrors}
				<div class="release-warning" class:confirmed={allowReleaseWithErrors} role="alert">
					<p>{releaseErrorText}</p>
					<Checkbox
						label={$t('CreateVersionDialog.AllowReleaseWithErrors')}
						bind:checked={allowReleaseWithErrors}
					/>
				</div>
			{/if}

			<div class="form-group">
				<BaseInput
					label={$t('CreateVersionDialog.VersionString')}
					value={form.releaseVersion}
					size="md"
					isRequired
					showError={!!releaseVersionError}
					errorMessage={releaseVersionError}
					mask="version"
					disabled={prefillLoading}
					append={prefillLoading
						? { component: LoadingSpinner, props: { size: 16, color: 'rgba(0, 44, 77, 0.5)' } }
						: null}
					onInput={(value) => (form.releaseVersion = value)}
					onBlur={handleReleaseVersionBlur}
					placeholder={$t('CreateVersionDialog.VersionStringPlaceholder')}
				/>
			</div>

			{#if showDevelopmentVersion}
				<div class="form-group">
					<BaseInput
						label={$t('CreateVersionDialog.DevelopmentVersion')}
						value={form.developmentVersion}
						size="md"
						isRequired
						showError={!!devVersionError}
						errorMessage={devVersionError}
						onInput={(value) => (form.developmentVersion = value)}
						onBlur={() => (touchedFields.developmentVersion = true)}
						placeholder={$t('CreateVersionDialog.VersionStringPlaceholder')}
					/>
				</div>
			{/if}

			<div class="changelog-field form-group">
				<MarkdownInput
					label={$t('CreateVersionDialog.Changelog')}
					bind:value={form.changelog}
					placeholder={$t('CreateVersionDialog.ChangelogPlaceholder')}
					enableImageUpload={false}
					maxHeight="220px"
				/>
			</div>

			<div class="button-group">
				<Button
					onclick={handleCreate}
					buttonText={$t('CreateVersionDialog.Create')}
					buttonType={ButtonType.BRAND}
					buttonSize={ButtonSize.LARGE}
					disabled={isSubmitDisabled}
				/>
				<Button
					onclick={onClose}
					buttonText={$t('CreateVersionDialog.Cancel')}
					buttonType={ButtonType.TERTIARY_INVERSE}
					buttonSize={ButtonSize.LARGE}
				/>
			</div>
		</div>
	{/snippet}
</Modal>

<style>
	.dialog-body {
		display: flex;
		flex-direction: column;
		gap: 1.25rem;
	}

	.form-group {
		display: flex;
		flex-direction: column;
	}

	.release-warning {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
		padding: 1rem;
		border: 1px solid #d92d20;
		border-radius: 8px;
		background: rgb(217 45 32 / 8%);
	}

	.release-warning.confirmed {
		border-color: #067a57;
		background: rgb(6 122 87 / 8%);
	}

	.release-warning p {
		margin: 0;
		color: #912018;
		font-weight: 600;
		line-height: 1.4;
	}

	.release-warning.confirmed p {
		color: #05603a;
	}

	.button-group {
		display: flex;
		justify-content: flex-start;
		gap: 0.75rem;
		margin-top: 0.5rem;
	}

	/* MarkdownInput wraps label + editor; tighten top spacing inside dialog */
	.changelog-field :global(.label-wrapper .editor-wrapper) {
		margin-top: 0;
	}
</style>
